import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ShieldCheck, Clock, CheckCircle, XCircle, AlertCircle, Upload, Trash2, FileText, Mail, QrCode, Smartphone, RefreshCcw, Camera, Link2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const CAPTURE_STORAGE_KEY = 'jjuma_kyc_capture_token';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to read file'));
    reader.readAsDataURL(file);
  });
}

const STATUS_CONFIG = {
  not_submitted: { icon: AlertCircle, color: 'text-slate-500', bg: 'bg-slate-50', label: 'Not Submitted' },
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Pending Review' },
  verified: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Verified' },
  rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Rejected' },
  more_info_required: { icon: AlertCircle, color: 'text-blue-600', bg: 'bg-blue-50', label: 'More Info Required' },
};

function FileUploadField({ fieldConfig, userId, onUploaded, existingDoc }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isSelfieField = Boolean(fieldConfig.capture_only) || String(fieldConfig.name || '').toLowerCase().includes('selfie');

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const b64 = String(dataUrl).split(',')[1] || '';
      const fileType = file.type || 'image/jpeg';
      const r = await api.post('/verification/upload-document', {
        document_name: fieldConfig.name,
        file_name: file.name,
        file_type: fileType,
        file_data: b64,
      });
      toast.success(`${fieldConfig.name} uploaded`);
      onUploaded(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingDoc) return;
    try {
      await api.delete(`/verification/document/${existingDoc.id}`);
      toast.success('Document removed');
      onUploaded(null);
    } catch {}
  };

  return (
    <div className="p-3 rounded-xl border border-slate-200 bg-white" data-testid={`upload-field-${fieldConfig.name.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-medium text-slate-900">{fieldConfig.name} {fieldConfig.required && <span className="text-red-500">*</span>}</p>
          {fieldConfig.help_text && <p className="text-xs text-slate-400">{fieldConfig.help_text}</p>}
        </div>
        {existingDoc && (
          <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Uploaded</span>
        )}
      </div>
      {existingDoc ? (
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-slate-400" />
          <span className="text-sm text-slate-700 flex-1 truncate">{existingDoc.file_name}</span>
          <span className="text-xs text-slate-400">{(existingDoc.file_size / 1024).toFixed(0)} KB</span>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-500 hover:text-red-700">
            <Trash2 size={14} />
          </Button>
        </div>
      ) : (
        <div>
          {isSelfieField ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
              Selfie verification must be captured with the phone camera session above.
            </div>
          ) : (
            <>
              <input type="file" ref={fileRef} onChange={handleUpload} accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full justify-center">
                <Upload size={14} className="mr-1.5" /> {uploading ? 'Uploading...' : 'Choose File'}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CaptureSessionPanel({ session, qrBase64, mobileUrl, creating, onStart, onRefresh }) {
  const requiredDocs = session?.required_documents || [];
  const uploadedSteps = new Set((session?.documents || []).map(doc => String(doc.capture_step || '').toLowerCase()));
  const completedCount = requiredDocs.filter(doc => uploadedSteps.has(doc.key)).length;
  const isExpired = session?.status === 'expired' || session?.expired;
  const isCompleted = session?.status === 'completed';
  const progressPercent = requiredDocs.length ? Math.round((completedCount / requiredDocs.length) * 100) : 0;
  const nextStep = requiredDocs.find(step => !uploadedSteps.has(step.key)) || requiredDocs[requiredDocs.length - 1];

  const copyMobileLink = async () => {
    if (!mobileUrl || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(mobileUrl);
      toast.success('Mobile link copied');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-3xl overflow-hidden">
      <div className="bg-gradient-to-r from-slate-950 via-emerald-900 to-emerald-700 px-6 py-5 text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-emerald-50">
              <Smartphone size={12} /> Desktop handoff
            </div>
            <h3 className="mt-3 text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Scan with your phone to continue KYC</h3>
            <p className="mt-2 text-sm text-emerald-50/85">
              Keep this page open on desktop, scan the QR with your phone, and complete document capture on a camera-friendly screen.
            </p>
          </div>
          {session?.status && (
            <span className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-medium capitalize ${isCompleted ? 'bg-emerald-400/20 text-emerald-50 border border-emerald-300/20' : isExpired ? 'bg-white/10 text-white border border-white/10' : 'bg-amber-300/20 text-amber-50 border border-amber-200/20'}`}>
              {session.status}
            </span>
          )}
        </div>
      </div>

      <CardContent className="p-6 space-y-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Progress</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{completedCount}/{requiredDocs.length} steps</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Next step</p>
            <p className="mt-1 text-sm font-semibold text-slate-900 truncate">{nextStep?.name || 'All done'}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Expires</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{session?.expires_at ? new Date(session.expires_at).toLocaleString() : 'Soon'}</p>
          </div>
        </div>

        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {!session ? (
          <div className="p-5 rounded-3xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500 space-y-3">
            <p className="font-medium text-slate-700">Create a secure mobile session for this verification.</p>
            <p className="text-xs text-slate-500">Your QR code will appear here and the phone capture flow will open with the correct steps.</p>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onStart} disabled={creating}>
              {creating ? 'Creating Session...' : 'Create QR Session'}
            </Button>
          </div>
        ) : (
          <>
            {qrBase64 && !isCompleted && !isExpired && (
              <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 items-stretch">
                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">QR code</p>
                    <div className="mt-3 rounded-2xl bg-white p-3 shadow-sm">
                      <img src={`data:image/png;base64,${qrBase64}`} alt="Verification QR code" className="w-full h-auto rounded-xl" data-testid="kyc-qr-code" />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={onRefresh} className="border-slate-200">
                      <RefreshCcw size={14} className="mr-1.5" /> Refresh QR
                    </Button>
                    <Button variant="outline" size="sm" onClick={copyMobileLink} className="border-slate-200">
                      <Copy size={14} className="mr-1.5" /> Copy link
                    </Button>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Mobile link</p>
                      <p className="mt-1 text-sm font-medium text-slate-900 break-all mono-nums">{mobileUrl}</p>
                    </div>
                    <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                      Camera ready
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {[
                      'Open the QR code or copy the link to your phone.',
                      'Capture each document with the phone camera.',
                      'Retake any step before uploading if needed.',
                      'The dashboard updates automatically when complete.',
                    ].map((item, index) => (
                      <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                          {index + 1}
                        </span>
                        <p className="text-sm text-slate-600">{item}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                    <div className="flex items-center gap-2 text-emerald-800">
                      <Link2 size={16} />
                      <p className="text-sm font-semibold">Need the link on your phone?</p>
                    </div>
                    <p className="mt-1 text-xs text-emerald-800/80">Copy the mobile URL and open it on the same device or send it to the phone you’re using for capture.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {requiredDocs.map(step => {
                const uploaded = uploadedSteps.has(step.key);
                return (
                  <div key={step.key} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{step.name}</p>
                      <p className="text-xs text-slate-500">{step.key === 'selfie' ? 'Capture with the front camera.' : 'Use the phone camera to scan this document.'}</p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${uploaded ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                      {uploaded ? 'Uploaded' : 'Pending'}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              {!isCompleted && !isExpired ? (
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onStart} disabled={creating}>
                  {creating ? 'Creating Session...' : 'Create New Session'}
                </Button>
              ) : null}
              {isExpired && (
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onStart} disabled={creating}>
                  {creating ? 'Creating Session...' : 'Create New Session'}
                </Button>
              )}
              {!isExpired && !isCompleted && mobileUrl && (
                <Button variant="outline" onClick={copyMobileLink} className="border-slate-200">
                  <Copy size={14} className="mr-1.5" /> Copy mobile link
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function Verification() {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [vType, setVType] = useState('personal');
  const [personalForm, setPersonalForm] = useState({ full_name: '', date_of_birth: '', national_id: '', document_type: 'national_id', phone: '', email: '', address: '' });
  const [businessForm, setBusinessForm] = useState({ business_name: '', registration_number: '', tin: '', business_type: '', contact_person: '', phone: '', email: '', address: '', business_address: '', website: '', authorized_person: '' });
  const [fieldConfig, setFieldConfig] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [supportEmail, setSupportEmail] = useState('verification@jjuma.com');
  const [accountType, setAccountType] = useState('individual');
  const [captureSession, setCaptureSession] = useState(null);
  const [captureQr, setCaptureQr] = useState('');
  const [captureLoading, setCaptureLoading] = useState(false);
  const [captureRefreshing, setCaptureRefreshing] = useState(false);
  const [captureToken, setCaptureToken] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem(CAPTURE_STORAGE_KEY) || '' : ''));

  const clearCaptureSession = useCallback(() => {
    setCaptureSession(null);
    setCaptureQr('');
    setCaptureToken('');
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CAPTURE_STORAGE_KEY);
    }
  }, []);

  const fetchData = useCallback(() => {
    api.get('/verification').then(r => {
      const d = r.data;
      setVerification(d);
      if (d.personal_info) setPersonalForm(prev => ({ ...prev, ...d.personal_info }));
      if (d.business_info) setBusinessForm(prev => ({ ...prev, ...d.business_info }));
      if (d.account_type) setAccountType(d.account_type);
      if (d.type) setVType(d.type);
      else if (d.account_type === 'business') setVType('business');
      setFieldConfig(d.field_config || []);
      setUploadedDocs(d.uploaded_documents || []);
      setSupportEmail(d.support_email || 'verification@jjuma.com');
      if (d.capture_session) {
        setCaptureSession(d.capture_session);
        if (d.capture_session.token && !captureToken) {
          setCaptureToken(d.capture_session.token);
          if (typeof window !== 'undefined') {
            localStorage.setItem(CAPTURE_STORAGE_KEY, d.capture_session.token);
          }
        }
        if (d.capture_session.status === 'completed') {
          clearCaptureSession();
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [captureToken, clearCaptureSession]);

  const loadCaptureSession = useCallback(async (token) => {
    if (!token) return;
    try {
      setCaptureRefreshing(true);
      const { data } = await api.get(`/verification/capture-session/${token}`);
      setCaptureSession(data);
      if (data.status === 'completed') {
        clearCaptureSession();
        fetchData();
      }
      if (data.status === 'expired') {
        toast.error('The KYC capture session has expired. Please create a new one.');
        clearCaptureSession();
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 404 || status === 410) {
        toast.error(detail || 'The KYC capture session has expired.');
        clearCaptureSession();
      } else if (detail) {
        toast.error(detail);
      }
    } finally {
      setCaptureRefreshing(false);
    }
  }, [clearCaptureSession, fetchData]);

  const loadCaptureQr = useCallback(async (token) => {
    if (!token) return;
    try {
      const { data } = await api.get(`/verification/capture-session/${token}/qr`);
      const activeToken = typeof window !== 'undefined' ? localStorage.getItem(CAPTURE_STORAGE_KEY) || '' : token;
      if (activeToken !== token) return;
      setCaptureQr(data.qr_base64 || '');
      setCaptureSession(prev => prev ? { ...prev, mobile_url: data.url || prev.mobile_url, status: data.status || prev.status, expires_at: data.expires_at || prev.expires_at } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to load QR code');
    }
  }, []);

  const startCaptureSession = useCallback(async () => {
    setCaptureLoading(true);
    try {
      const payload = { document_type: personalForm.document_type || 'national_id', personal_info: personalForm };
      const { data } = await api.post('/verification/capture-session', payload);
      const token = data.token;
      setCaptureToken(token);
      setCaptureSession(data);
      if (typeof window !== 'undefined') {
        localStorage.setItem(CAPTURE_STORAGE_KEY, token);
      }
      await loadCaptureQr(token);
      toast.success('QR capture session created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to create capture session');
    } finally {
      setCaptureLoading(false);
    }
  }, [loadCaptureQr, personalForm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!captureToken) return;
    loadCaptureSession(captureToken);
    loadCaptureQr(captureToken);
  }, [captureToken, loadCaptureQr, loadCaptureSession]);

  useEffect(() => {
    if (!captureToken) return undefined;
    const timer = setInterval(() => loadCaptureSession(captureToken), 5000);
    return () => clearInterval(timer);
  }, [captureToken, loadCaptureSession]);

  const handleDocUploaded = (fieldName, doc) => {
    if (doc) {
      setUploadedDocs(prev => [...prev.filter(d => d.document_name !== fieldName), doc]);
    } else {
      setUploadedDocs(prev => prev.filter(d => d.document_name !== fieldName));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { type: vType, ...(vType === 'personal' ? personalForm : businessForm) };
      await api.post('/verification', payload);
      toast.success('Verification submitted!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;

  const status = verification?.status || 'not_submitted';
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;
  const canEdit = ['not_submitted', 'rejected', 'more_info_required'].includes(status);

  const filteredFields = fieldConfig.filter(f => f.applies_to === vType || f.applies_to === 'both');

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Verification</h1>
        <p className="text-slate-500 text-sm mt-0.5">Verify your identity to unlock all features</p>
      </div>

      <div className={`p-4 rounded-xl ${config.bg} border flex items-center gap-3`} data-testid="verification-status-banner">
        <StatusIcon size={20} className={config.color} />
        <div>
          <p className={`text-sm font-medium ${config.color}`}>{config.label}</p>
          {status === 'pending' && <p className="text-xs text-slate-500 mt-0.5">Your documents are being reviewed</p>}
          {status === 'verified' && <p className="text-xs text-slate-500 mt-0.5">Your account is fully verified</p>}
          <p className="text-xs text-slate-500 mt-0.5">Account type: {accountType === 'business' ? 'Business / Company' : 'Individual / Normal'}</p>
          {verification?.admin_notes && <p className="text-xs text-slate-600 mt-1">Admin note: {verification.admin_notes}</p>}
        </div>
      </div>

      {canEdit && (
        <>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-6">
              <Tabs value={vType} onValueChange={setVType}>
                <TabsList className="mb-4">
                  <TabsTrigger value="personal" data-testid="personal-tab">Personal</TabsTrigger>
                  <TabsTrigger value="business" data-testid="business-tab">Business / Organisation</TabsTrigger>
                </TabsList>

                <form onSubmit={handleSubmit}>
                  <TabsContent value="personal" className="space-y-4">
                    <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm">
                      Individual accounts can upload National ID or Passport documents and complete live selfie verification.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><Label>Full Name</Label><Input data-testid="v-full-name" value={personalForm.full_name} onChange={e => setPersonalForm(p => ({ ...p, full_name: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Date of Birth</Label><Input data-testid="v-dob" type="date" value={personalForm.date_of_birth} onChange={e => setPersonalForm(p => ({ ...p, date_of_birth: e.target.value }))} className="mt-1" required /></div>
                      <div>
                        <Label>Document Type</Label>
                        <Select value={personalForm.document_type} onValueChange={v => setPersonalForm(p => ({ ...p, document_type: v }))}>
                          <SelectTrigger className="mt-1" data-testid="v-doc-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="national_id">National ID</SelectItem>
                            <SelectItem value="passport">Passport</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>ID/Document Number</Label><Input data-testid="v-national-id" value={personalForm.national_id} onChange={e => setPersonalForm(p => ({ ...p, national_id: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Phone</Label><Input data-testid="v-phone" value={personalForm.phone} onChange={e => setPersonalForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Email</Label><Input data-testid="v-email" type="email" value={personalForm.email} onChange={e => setPersonalForm(p => ({ ...p, email: e.target.value }))} className="mt-1" required /></div>
                      <div className="sm:col-span-2"><Label>Address</Label><Input data-testid="v-address" value={personalForm.address} onChange={e => setPersonalForm(p => ({ ...p, address: e.target.value }))} className="mt-1" required /></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="business" className="space-y-4">
                    <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-blue-800 text-sm">
                      Business accounts should upload company registration documents, tax information, director ID, business address, and authorized person details.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><Label>Business Name</Label><Input data-testid="v-biz-name" value={businessForm.business_name} onChange={e => setBusinessForm(p => ({ ...p, business_name: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Registration Number</Label><Input data-testid="v-biz-reg" value={businessForm.registration_number} onChange={e => setBusinessForm(p => ({ ...p, registration_number: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>TIN</Label><Input value={businessForm.tin} onChange={e => setBusinessForm(p => ({ ...p, tin: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Business Type</Label><Input value={businessForm.business_type} onChange={e => setBusinessForm(p => ({ ...p, business_type: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Contact Person</Label><Input value={businessForm.contact_person} onChange={e => setBusinessForm(p => ({ ...p, contact_person: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Phone</Label><Input value={businessForm.phone} onChange={e => setBusinessForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Email</Label><Input type="email" value={businessForm.email} onChange={e => setBusinessForm(p => ({ ...p, email: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Business Address</Label><Input value={businessForm.business_address} onChange={e => setBusinessForm(p => ({ ...p, business_address: e.target.value }))} className="mt-1" required /></div>
                      <div><Label>Website or Social Page</Label><Input value={businessForm.website} onChange={e => setBusinessForm(p => ({ ...p, website: e.target.value }))} className="mt-1" placeholder="https://..." /></div>
                      <div><Label>Authorized Person</Label><Input value={businessForm.authorized_person} onChange={e => setBusinessForm(p => ({ ...p, authorized_person: e.target.value }))} className="mt-1" required /></div>
                      <div className="sm:col-span-2"><Label>Address</Label><Input value={businessForm.address} onChange={e => setBusinessForm(p => ({ ...p, address: e.target.value }))} className="mt-1" required /></div>
                    </div>
                  </TabsContent>

                  <Button type="submit" data-testid="submit-verification-btn" disabled={submitting} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white">
                    {submitting ? 'Submitting...' : 'Submit for Verification'}
                  </Button>
                </form>
              </Tabs>
            </CardContent>
          </Card>

          {vType === 'personal' && (
            <CaptureSessionPanel
              session={captureSession}
              qrBase64={captureQr}
              mobileUrl={captureSession?.mobile_url || ''}
              creating={captureLoading || captureRefreshing}
              onStart={startCaptureSession}
              onRefresh={() => captureToken && loadCaptureQr(captureToken)}
            />
          )}

          {/* Document Upload Section */}
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Upload Documents</h3>
              <p className="text-sm text-slate-500 mb-4">Upload required verification documents (JPEG, PNG, WebP, PDF, max 5MB each)</p>
              <div className="space-y-3">
                {filteredFields.filter(f => f.type === 'file').map(field => (
                  <FileUploadField
                    key={field.name}
                    fieldConfig={field}
                    existingDoc={uploadedDocs.find(d => d.document_name === field.name)}
                    onUploaded={(doc) => handleDocUploaded(field.name, doc)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-sm flex items-start gap-3">
            <Mail size={18} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Having trouble uploading?</p>
              <p className="mt-0.5">You can also submit your documents by email to <a href={`mailto:${supportEmail}`} className="font-semibold underline" data-testid="support-email-link">{supportEmail}</a></p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
